import type {
    ExclusiveZone,
    ExclusiveZoneData,
    ExclusiveLaunchLayout,
    ExclusiveLaunchGame,
    ExclusiveLaunchContent
} from '@/types/zones/exclusive-zone';
import type { Orientation } from '@/types/constants';
import { ORIENTATIONS } from '@/utils/constants';

type ValidatedLayout = ExclusiveLaunchLayout & { primary: ExclusiveLaunchContent; id: string };
type ValidatedGame = ExclusiveLaunchGame & { data: NonNullable<ExclusiveLaunchGame['data']> };

export type ValidPreLaunchContent = {
    zoneContent: ExclusiveZoneData;
    layoutContent: ValidatedLayout;
    gameContent: ValidatedGame;
    backgroundUrl: string;
};

export type ValidPostLaunchContent = {
    zoneContent: ExclusiveZoneData;
    gameContent: ValidatedGame;
};

const hasNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

export const validatePreLaunchContent = (
    zone: ExclusiveZone | undefined,
    layoutVariant: Orientation
): ValidPreLaunchContent | null => {
    const zoneContent = zone?.data;

    if (zoneContent && zoneContent.show_module_in_lobby !== true) return null;

    const layoutContent = zoneContent?.[layoutVariant]?.[0];
    const gameContent = zoneContent?.select_game;
    const backgroundUrl = layoutContent?.primary?.background?.url;

    const isValid =
        !!zoneContent &&
        !!layoutContent?.primary &&
        hasNonEmptyString(layoutContent?.id) &&
        !!gameContent?.data &&
        hasNonEmptyString(backgroundUrl);

    if (!isValid) {
        console.warn('[exclusive-zone] data is missing from Prismic', { zoneUid: zone?.uid });
        return null;
    }

    return {
        zoneContent: zoneContent as ExclusiveZoneData,
        layoutContent: layoutContent as ValidatedLayout,
        gameContent: gameContent as ValidatedGame,
        backgroundUrl: backgroundUrl as string
    };
};

export const validatePostLaunchContent = (zone: ExclusiveZone | undefined): ValidPostLaunchContent | null => {
    const zoneContent = zone?.data;
    const gameContent = zoneContent?.select_game;
    const portraitLayout = zoneContent?.[ORIENTATIONS.PORTRAIT]?.[0];
    const landscapeLayout = zoneContent?.[ORIENTATIONS.LANDSCAPE]?.[0];

    const isValid = !!zoneContent && !!gameContent?.data && !!portraitLayout?.primary && !!landscapeLayout?.primary;

    if (!isValid) {
        console.warn('[exclusive-zone] data is missing from Prismic', { zoneUid: zone?.uid });
        return null;
    }

    return { zoneContent: zoneContent as ExclusiveZoneData, gameContent: gameContent as ValidatedGame };
};
